'use client';

import { useClientTranslation } from '@lib/i18n/client';
import {
  ManifestForm,
  ManifestSalutation,
  ManifestPreliminaryStatusView,
  phoneToApi,
  type ManifestFormProps,
  type ManifestPreliminaryStatusViewProps,
} from '@modules/manifest';
import { invokePrismAction } from '@modules/prism-client/lib/invoke-prism-action';
import { hashPin } from '@modules/prism-client/lib/manifest/pin-hash';
import {
  getPersistedPinHash,
  setPersistedPinHash,
} from '@modules/prism-client/lib/manifest/pin-storage';
import { Button } from '@tuic/web-compositions/core/Button';
import { useCallback, useEffect, useState } from 'react';

import type { ManifestFormValues } from '@modules/manifest';
import type PrismAPI from '@modules/prism-client/types/prism-types';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { Account } from '@tuic/api-kit';
import type { FC } from 'react';

const PIN_LENGTH = 4;

export type ManifestFormBlockProps = ManifestFormProps & {
  traveller: { mpi: number; pinHash?: string };
  submitAction: PrismAPI.ActionDefinition;
  showEtaCheckbox: boolean;
  showUsaCheckbox: boolean;
  successTitle?: ManifestPreliminaryStatusViewProps['title'];
  successParagraph?: ManifestPreliminaryStatusViewProps['paragraph'];
  successPictogram?: ManifestPreliminaryStatusViewProps['pictogram'];
  successPinLabel?: ManifestPreliminaryStatusViewProps['pinLabel'];
  backToOverviewLabel?: string;
  backToOverviewHref?: string;
};

const sanitizeSection = (
  section: Record<string, unknown>,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(section)) {
    const value = section[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      out[key] = trimmed === '' ? null : trimmed;
    } else {
      out[key] = value;
    }
  }
  return out;
};

const reshapeOtherInformation = (
  values: ManifestFormValues['otherInformation'],
  salutation: string,
): Account.ManifestOtherInformation => {
  const {
    mealRequest,
    individualRequest,
    allergy,
    allergyInfo,
    emergencyContactName,
    emergencyContactPhone,
    handicap,
    acceptDataProtectionTerms,
    acceptHealthTerms,
    pregnant24Weeks,
  } = values;

  // TBD: backend enforces 'NotPregnant24Weeks must be True'. Non-female
  // travellers don't see the checkbox (see OtherInformationSection), so we
  // hardcode `true` to keep the manifest savable. Remove once the backend
  // accepts `null` or relaxes the rule.
  const isFemale = salutation === ManifestSalutation.FRAU;

  // Send health-data consent only when at least one health field (diet or
  // handicap) is filled. If all five are empty, send `null` — even if the
  // user had consented earlier and then cleared the fields.
  const hasHealthData =
    !!mealRequest.trim() ||
    !!individualRequest.trim() ||
    allergy.length > 0 ||
    !!allergyInfo.trim() ||
    !!handicap.trim();

  return {
    handicap: handicap.trim() || null,
    acceptDataProtectionTerms: hasHealthData ? acceptDataProtectionTerms : null,
    acceptHealthTerms,
    pregnant24Weeks: isFemale ? pregnant24Weeks : true,
    dietary: {
      mealRequest: mealRequest.trim() || null,
      individualRequest: individualRequest.trim() || null,
      allergy: allergy.length > 0 ? allergy : undefined,
      allergyInfo: allergyInfo.trim() || null,
    },
    emergencyContact: {
      name: emergencyContactName.trim() || null,
      phone: phoneToApi(emergencyContactPhone),
    },
  };
};

type CheckboxVisibility = {
  showUsaCheckbox: boolean;
  showEtaCheckbox: boolean;
};

// Cast: `sanitizeSection` returns `Record<string, unknown>` to keep the helper
// generic; every key it produces is in fact a valid `ManifestDetail` section
// field (reshapeForApi enumerates the sections exhaustively).
//
// `hasVisa` / `hasETA` are nulled out when their checkbox wasn't rendered,
// preserving the backend's "not applicable" signal (otherwise we would send
// `false` for fields that don't apply to this passenger).
const reshapeForApi = (
  values: ManifestFormValues,
  visibility: CheckboxVisibility,
): Account.ManifestDetail => {
  const credentials = sanitizeSection(values.credentials);
  if (!visibility.showUsaCheckbox) credentials.hasVisa = null;
  if (!visibility.showEtaCheckbox) credentials.hasETA = null;

  const contact = sanitizeSection(values.contact);
  contact.phone = phoneToApi(values.contact.phone);
  contact.mobile = phoneToApi(values.contact.mobile);

  return {
    personalInfo: sanitizeSection(values.personalInfo),
    contact,
    credentials,
    otherInformation: reshapeOtherInformation(
      values.otherInformation,
      values.personalInfo.salutation,
    ),
    preferredLanguageCode:
      values.otherInformation.preferredLanguageCode || null,
  } as Account.ManifestDetail;
};

export const ManifestFormBlock: ComponentBlock<ManifestFormBlockProps> = ({
  data,
  fallback,
}) => {
  if (fallback) {
    return <ManifestForm fallback />;
  }

  if (!data) return null;

  return <ManifestFormBlockContent {...data} />;
};

const ManifestFormBlockContent: FC<ManifestFormBlockProps> = data => {
  const { t, i18n } = useClientTranslation();
  const [isPinRequired, setIsPinRequired] = useState<boolean | undefined>(
    undefined,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedPin, setSubmittedPin] = useState<string | undefined>(
    undefined,
  );
  // Surface any non-2xx backend response in the form-level alert.
  // We deliberately don't try to map server errors back onto individual
  // fields — client-side Zod owns the field highlighting; the backend's
  // own message just appears in the top alert.
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!data.traveller?.pinHash) {
      setIsPinRequired(false);
      return;
    }
    const persistedPinHash = getPersistedPinHash(data.traveller.mpi);
    setIsPinRequired(data.traveller.pinHash !== persistedPinHash);
  }, [data.traveller?.pinHash, data.traveller?.mpi]);

  const handlePinEnter = (enteredPin: string): boolean => {
    if (!data.traveller?.pinHash) return false;

    const enteredHash = hashPin(enteredPin);
    if (data.traveller.pinHash !== enteredHash) return false;

    setPersistedPinHash(data.traveller.mpi, enteredHash);
    setIsPinRequired(false);
    return true;
  };

  const handleSubmit = useCallback(
    async (formValues: ManifestFormValues) => {
      if (!data.traveller?.mpi) {
        return;
      }

      setServerErrorMessage(null);
      setIsSubmitting(true);

      const body = reshapeForApi(formValues, {
        showUsaCheckbox: data.showUsaCheckbox,
        showEtaCheckbox: data.showEtaCheckbox,
      });

      try {
        const result = await invokePrismAction<PrismAPI.StoreManifestAction>(
          data.submitAction,
          body,
          i18n.resolvedLanguage,
        );

        if (result.success) {
          const submittedPassportNumber =
            formValues.credentials?.passportNumber ?? '';
          setSubmittedPin(
            submittedPassportNumber
              ? submittedPassportNumber.slice(-PIN_LENGTH)
              : undefined,
          );
          setIsSubmitted(true);
          return;
        }

        setServerErrorMessage(result.error?.message ?? null);
      } catch {
        // Network drop, JSON parse, or any other runtime failure inside
        // `invokePrismAction`. Surface a generic message so the user sees
        // why nothing happened — without leaving the form locked.
        setServerErrorMessage(
          t(
            'Leider haben wir aktuell technische Probleme. Bitte versuchen Sie es in wenigen Minuten erneut. Bitte entschuldigen Sie die Unannehmlichkeiten!',
          ),
        );
      } finally {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setIsSubmitting(false);
      }
    },
    [
      data.traveller?.mpi,
      data.submitAction,
      data.showUsaCheckbox,
      data.showEtaCheckbox,
      i18n.resolvedLanguage,
      t,
    ],
  );

  const successView = isSubmitted ? (
    <ManifestPreliminaryStatusView
      title={data.successTitle}
      paragraph={data.successParagraph}
      pictogram={data.successPictogram}
      pinLabel={data.successPinLabel}
      pin={submittedPin}
      action={
        data.backToOverviewHref && data.backToOverviewLabel ? (
          <Button asChild>
            <a href={data.backToOverviewHref}>{data.backToOverviewLabel}</a>
          </Button>
        ) : undefined
      }
    />
  ) : undefined;

  return (
    <ManifestForm
      {...data}
      manifest={data.manifest}
      fallback={isPinRequired !== false && !isSubmitted}
      isPinRequired={isPinRequired}
      onPinEnter={handlePinEnter}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      serverErrorMessage={serverErrorMessage}
      successView={successView}
    />
  );
};
